import { useState, useEffect, useCallback, useRef } from 'react';
import { useAIToolsStore } from '../stores/aiToolsStore';

/**
 * Drop-in replacement for useState that persists to Zustand store.
 * Usage: const [val, setVal] = usePersistedInput('seo', 'niche', '');
 */
export function usePersistedInput(toolId, key, defaultValue = '') {
  const store = useAIToolsStore();
  const persisted = store.tools[toolId]?.inputs?.[key];
  const [value, setValue] = useState(persisted !== undefined ? persisted : defaultValue);
  const isInit = useRef(true);

  useEffect(() => {
    if (isInit.current) {
      isInit.current = false;
      return;
    }
    store.setToolInput(toolId, key, value);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  return [value, setValue];
}

/**
 * Persists written for you output across tab switches.
 * Usage: const [data, setData] = usePersistedOutput('seo', null);
 */
export function usePersistedOutput(toolId, defaultValue = null) {
  const store = useAIToolsStore();
  const persisted = store.tools[toolId]?.output;
  const [value, setValue] = useState(persisted !== undefined ? persisted : defaultValue);
  const isInit = useRef(true);

  useEffect(() => {
    if (isInit.current) {
      isInit.current = false;
      return;
    }
    if (value !== null && value !== undefined) {
      store.setToolOutput(toolId, value);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const setter = useCallback((v) => {
    setValue(v);
  }, []);

  return [value, setter];
}
